import { useInfiniteLoadingResource } from "source/shared/hooks/useInfiniteLoadingResource"
import { useApiRead } from "source/shared/SessionApiResource"

export type Event = {
  attributes: {
    church_center_web_url: string
    church_center_my_event_url: string
    starts_at: string
  }
}

export type CrossAppEvent = {
  attributes: Event["attributes"] & {
    ends_at: string
    name: string
    description: string
    source: {
      app: string
    }
  }
}

export type CrossAppEventResponse = {
  data: CrossAppEvent[]
  meta: {
    total_count: number
  }
}

type Props = {
  eventsUrl: string
  crossAppUrl: string
  filter: "upcoming" | "past"
  publicPage: boolean
}

export function useCrossAppEvents({
  eventsUrl,
  crossAppUrl,
  filter,
  publicPage,
}: Props) {
  const { records: events = [], ...rest } =
    useInfiniteLoadingResource(eventsUrl)
  const { data: crossAppEvents } = useApiRead(
    crossAppUrl,
  ) as CrossAppEventResponse
  const getEventUrl = (event: Event) => {
    if (publicPage) {
      return event.attributes.church_center_web_url
    }

    return (
      event.attributes.church_center_my_event_url ||
      event.attributes.church_center_web_url
    )
  }

  const lastTimestamp = events.at(-1)?.attributes.starts_at
  const promotedEvents = crossAppEvents.filter((event: CrossAppEvent) => {
    if (!rest.hasMore || !lastTimestamp) {
      return true
    }

    const withinRange =
      filter === "upcoming"
        ? new Date(event.attributes.starts_at).valueOf() <
          new Date(lastTimestamp).valueOf()
        : new Date(event.attributes.starts_at).valueOf() >
          new Date(lastTimestamp).valueOf()

    return withinRange
  })
  const newEvents =
    promotedEvents.length > 0
      ? events
          .concat(promotedEvents)
          .sort(
            filter === "upcoming"
              ? (a: Event, b: Event) =>
                  new Date(a.attributes.starts_at).valueOf() -
                  new Date(b.attributes.starts_at).valueOf()
              : (a: Event, b: Event) =>
                  new Date(b.attributes.starts_at).valueOf() -
                  new Date(a.attributes.starts_at).valueOf(),
          )
      : events

  return { records: newEvents, getEventUrl, ...rest }
}
